import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { Box, Typography, Switch } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import monitorAlertsService from '../../../services/monitorAlertsService';
import { StyledDataGridContainer } from '../../common/StyledDataGridContainer';

const MonitorAlerts = ({ showNotification }) => {
	const { tenantId } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [monitorSourceTypes, setMonitorSourceTypes] = useState([]);
	const [configListId, setConfigListId] = useState(null);

	// Fetch monitor source types
	const fetchData = useCallback(async () => {
		if (!tenantId) return;

		setIsLoading(true);
		try {
			// Fetch monitor source types
			const sourceTypes =
				await monitorAlertsService.getMonitorSourceTypes(tenantId);

			setMonitorSourceTypes(sourceTypes);

			// If we have source types, set the config list ID from the first item
			if (sourceTypes.length > 0) {
				setConfigListId(sourceTypes[0]?.config_list_id);
			}
		} catch (error) {
			console.error('Error fetching monitor alerts data:', error);
			showNotification('Failed to load monitor alerts data', 'error');
		} finally {
			setIsLoading(false);
		}
	}, [tenantId, showNotification]);

	const initialFetchRef = useRef(false);

	useEffect(() => {
		if (tenantId && !initialFetchRef.current) {
			initialFetchRef.current = true;
			fetchData();
		}
	}, [tenantId, fetchData]);

	const updateMonitorSource = useCallback(
		async (monitorSource, previousState) => {
			// Optimistically update the UI
			setMonitorSourceTypes((prevTypes) =>
				prevTypes.map((type) =>
					type.id === monitorSource.id ? monitorSource : type
				)
			);

			try {
				await monitorAlertsService.updateMonitorSourceType(
					tenantId,
					configListId,
					monitorSource.id,
					{
						active: monitorSource.active,
					}
				);
				showNotification('Monitor source updated successfully');
			} catch (error) {
				console.error('Error updating monitor source:', error);
				// Revert to previous state on error
				if (previousState) {
					setMonitorSourceTypes((prevTypes) =>
						prevTypes.map((type) =>
							type.id === previousState.id ? previousState : type
						)
					);
				}
				showNotification('Failed to update monitor source', 'error');
				throw error;
			}
		},
		[tenantId, configListId, showNotification]
	);

	const handleActiveToggle = (event, row) => {
		const updatedRow = { ...row, active: event.target.checked };
		updateMonitorSource(updatedRow, { ...row }); // Pass current state as previousState
	};

	const columns = [
		{
			field: 'display_name',
			headerName: 'Monitor Source',
			flex: 1,
		},
		{
			field: 'description',
			headerName: 'Description',
			flex: 1,
		},
		{
			field: 'active',
			headerName: 'Active',
			width: 100,
			editable: false,
			renderCell: (params) => (
				<Switch
					checked={params.row.active}
					onChange={(e) => handleActiveToggle(e, params.row)}
					color='primary'
				/>
			),
		},
	];

	return (
		<Box sx={{ p: 3 }}>
			<Box
				display='flex'
				justifyContent='space-between'
				alignItems='center'
				mb={3}
			>
				<Typography variant='h6'>Monitor Alerts Configuration</Typography>
			</Box>

			<StyledDataGridContainer>
				<DataGrid
					rows={monitorSourceTypes}
					columns={columns}
					loading={isLoading}
					disableSelectionOnClick
					hideFooter
					autoHeight
					getRowId={(row) => row.id}
				/>
			</StyledDataGridContainer>
		</Box>
	);
};

export default MonitorAlerts;
